<div class="professionals-specific-data-container" [class.read-only-mode]="isReadOnly()">
  <!-- Toast Notifications -->
  <p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true" />

  <!-- Live Region for Announcements -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    Live region for accessibility announcements
  </div>

  <app-validation-error-summary
    [sections]="validationErrorSections()"
    (closed)="closeValidationSummary()"
  />

  <!-- Modification Request Banner -->
  @if (hasModificationRequest()) {
    <div class="modification-request-banner mb-4 p-4 border-round-lg surface-0 border-2 border-orange-500 shadow-3" role="alert" aria-live="assertive">
      <div class="flex align-items-start gap-3">
        <div class="flex-shrink-0">
          <i class="pi pi-exclamation-triangle text-orange-500 text-3xl" aria-hidden="true"></i>
        </div>
        <div class="flex-1">
          <h3 class="text-orange-900 font-bold text-xl m-0 mb-2">
            <i class="pi pi-refresh mr-2" aria-hidden="true"></i>
            Richiesta di Modifica
          </h3>
          <p class="text-orange-800 text-base m-0 mb-3">
            Il revisore ha richiesto delle modifiche alla tua registrazione. Si prega di leggere attentamente le note seguenti e apportare le correzioni necessarie.
          </p>
          @if (modificationComment()) {
            <div class="surface-50 border-round p-3 border-left-3 border-orange-500">
              <p class="text-sm font-semibold text-600 m-0 mb-2">
                <i class="pi pi-comment mr-2" aria-hidden="true"></i>
                Note del Revisore:
              </p>
              <p class="text-base text-900 m-0 white-space-pre-wrap">{{ displayedComment() }}</p>
              @if (isCommentLong()) {
                <button
                  type="button"
                  class="text-sm text-orange-600 hover:text-orange-700 font-semibold cursor-pointer border-none bg-transparent p-0 mt-2"
                  (click)="toggleModificationNotes()"
                  [attr.aria-expanded]="isModificationNotesExpanded()"
                  aria-controls="modification-notes">
                  <i class="pi mr-1" [class.pi-chevron-down]="!isModificationNotesExpanded()" [class.pi-chevron-up]="isModificationNotesExpanded()" aria-hidden="true"></i>
                  {{ isModificationNotesExpanded() ? 'Mostra meno' : 'Visualizza altro' }}
                </button>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Page Header (hidden in review mode) -->
  @if (!professionalIdInput()) {
    <section class="page-header" role="region" aria-labelledby="form-title">
      <div class="header-content">
        <div class="header-left">
          <i class="pi pi-building header-icon" aria-hidden="true"></i>
          <div class="header-text">
            <h1 id="form-title">Dati Specifici</h1>
            <p class="professional-info">
              @if (professionalSummaryBusinessName()) {
                <strong>{{ professionalSummaryBusinessName() }}</strong><br>
              }
              @if (professionalIdDisplay()) {
                ID: {{ professionalIdDisplay() }}
              }
              @if (professionalSummaryType()) {
                &nbsp;·&nbsp;{{ professionalSummaryType() }}
              }
            </p>
            <p class="subtitle">Inserisci i dati specifici per la tipologia selezionata</p>
          </div>
        </div>
        <!-- Completion Badge -->
        <div class="completion-badge" [attr.aria-label]="isStepCompleted() ? 'Step completato' : 'Step non completato'">
          @if (isStepCompleted()) {
            <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
            <span class="completion-text">Completato</span>
          } @else {
            <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
            <span class="completion-text">Da completare</span>
          }
        </div>
      </div>
    </section>
  }

  <!-- Read-Only Mode Banner (hidden in review mode) -->
  @if (isReadOnly() && !professionalIdInput()) {
    <div class="read-only-banner mb-4" role="alert">
      <div class="flex align-items-center gap-2 mb-2">
        <i class="pi pi-eye text-blue-600 text-xl" aria-hidden="true"></i>
        <strong class="text-blue-900">Modalità Sola Lettura</strong>
      </div>
      <span class="text-blue-700 text-sm">Stai visualizzando l'anagrafica inviata. I campi non sono modificabili.</span>
    </div>
  }

  <!-- Dynamic Form Content -->
  @if (tipologia() === 'AUTO_EXPERT') {
    <app-auto-expert-form
      [professionalIdInput]="professionalIdInput()"
      [readonly]="isReadOnly()"
      (onSave)="handleSave($event)"
      (onSaveDraft)="handleSaveDraft($event)"
      (validationFailed)="handleValidationFailed($event)"
    ></app-auto-expert-form>
  }
  @if (tipologia() === 'RE_EXPERT') {
    <app-perito-re-form
      [professionalIdInput]="professionalIdInput()"
      [readonly]="isReadOnly()"
      (onSave)="handleSave($event)"
      (onSaveDraft)="handleSaveDraft($event)"
      (validationFailed)="handleValidationFailed($event)"
    ></app-perito-re-form>
  }
  @if (tipologia() === 'INVESTIGATOR') {
    <app-investigator-form
      [professionalIdInput]="professionalIdInput()"
      [readonly]="isReadOnly()"
      (onSave)="handleSave($event)"
      (onSaveDraft)="handleSaveDraft($event)"
      (validationFailed)="handleValidationFailed($event)"
    ></app-investigator-form>
  }
  @if (tipologia() === 'DOCTOR') {
    <app-doctor-form
      [professionalIdInput]="professionalIdInput()"
      [readonly]="isReadOnly()"
      (onSave)="handleSave($event)"
      (onSaveDraft)="handleSaveDraft($event)"
      (validationFailed)="handleValidationFailed($event)"
    ></app-doctor-form>
  }
  @if (tipologia() === 'LAWYER') {
    <app-lawyer-form
      [professionalIdInput]="professionalIdInput()"
      [readonly]="isReadOnly()"
      (onSave)="handleSave($event)"
      (onSaveDraft)="handleSaveDraft($event)"
      (validationFailed)="handleValidationFailed($event)"
    ></app-lawyer-form>
  }
  @if (tipologia() === 'NETWORK') {
    <div class="no-tipologia-message">
      <i class="pi pi-info-circle" style="font-size: 3rem; color: #00125b;"></i>
      <h2>Nessuna sezione dati specifici</h2>
      <p>Per il tipo di fiduciario <strong>Network</strong> non è prevista la compilazione di dati specifici.</p>
      <button
        pButton
        type="button"
        label="Continua ai Questionari"
        icon="pi pi-arrow-right"
        iconPos="right"
        class="p-button-primary mt-3"
        (click)="skipToQuestionnaires()"
      ></button>
    </div>
  }
  @if (tipologia() === 'HAIL_PROVIDER') {
    <app-hail-provider-form
      (onSave)="handleSave($event)"
      (onSaveDraft)="handleSaveDraft($event)"
      (validationFailed)="handleValidationFailed($event)"
    ></app-hail-provider-form>
  }
  @if (
    (tipologia() === 'BODYSHOP' || tipologia() === 'NK_BODYSHOP') &&
    (tipologiaCarrozzeria() === 'STANDARD' ||
      tipologiaCarrozzeria() === 'ARS' ||
      tipologiaCarrozzeria() === 'RCI' ||
      tipologiaCarrozzeria() === 'AXA_DEUTSCHE_BANK')
  ) {
    <app-bodyshop-form
      [professionalIdInput]="getCurrentProfessionalId()"
      [readonly]="isReadOnly()"
      [tipologiaCarrozzeria]="tipologiaCarrozzeria()"
      [isNetworkBodyShop]="tipologia() === 'NK_BODYSHOP'"
      (onSave)="handleSave($event)"
      (onSaveDraft)="handleSaveDraft($event)"
      (validationFailed)="handleValidationFailed($event)"
    ></app-bodyshop-form>
  }

  <!-- Loading message while fetching tipologia -->
  @if (isLoadingTipologia()) {
    <div class="no-tipologia-message">
      <i class="pi pi-spin pi-spinner" style="font-size: 3rem; color: #00125b;"></i>
      <h2>Caricamento in corso...</h2>
      <p>Stiamo recuperando i dati del professionista.</p>
    </div>
  }

  <!-- Fallback message if no tipologia is set and not loading -->
  @if (!tipologia() && !isLoadingTipologia()) {
    <div class="no-tipologia-message">
      <i class="pi pi-info-circle" style="font-size: 3rem; color: #ffa500;"></i>
      <h2>Dati non disponibili</h2>
      <p>Non è possibile visualizzare i dati specifici in questo momento.</p>
      <p>Se il problema persiste, contatta l'assistenza.</p>
    </div>
  }

  <!-- Debug message if tipologia is BODYSHOP/NK_BODYSHOP but no valid subtype -->
  @if (
    (tipologia() === 'BODYSHOP' || tipologia() === 'NK_BODYSHOP') &&
    tipologiaCarrozzeria() !== 'STANDARD' &&
    tipologiaCarrozzeria() !== 'ARS' &&
    tipologiaCarrozzeria() !== 'RCI' &&
    tipologiaCarrozzeria() !== 'AXA_DEUTSCHE_BANK'
  ) {
    <div class="no-tipologia-message">
      <i class="pi pi-spin pi-spinner" style="font-size: 3rem; color: #00125b;"></i>
      <h2>Caricamento in corso...</h2>
      <p>Stiamo recuperando i dati specifici della carrozzeria.</p>
    </div>
  }

  <!-- Generic message for any unhandled tipologia -->
  @if (
    tipologia() &&
    tipologia() !== 'AUTO_EXPERT' &&
    tipologia() !== 'RE_EXPERT' &&
    tipologia() !== 'INVESTIGATOR' &&
    tipologia() !== 'DOCTOR' &&
    tipologia() !== 'LAWYER' &&
    tipologia() !== 'HAIL_PROVIDER' &&
    tipologia() !== 'BODYSHOP' &&
    tipologia() !== 'NK_BODYSHOP'
  ) {
    <div class="no-tipologia-message">
      <i class="pi pi-info-circle" style="font-size: 3rem; color: #ffa500;"></i>
      <h2>Sezione non disponibile</h2>
      <p>I dati specifici non sono disponibili per questa categoria di professionista.</p>
    </div>
  }

  <!-- Action Buttons (Read-Only Mode) -->
  @if (isReadOnly() && !professionalIdInput()) {
    <div class="card-footer mt-4">
      <div class="action-buttons">
        <button
          pButton
          type="button"
          label="Torna alla Progressione"
          icon="pi pi-arrow-left"
          class="p-button-secondary"
          (click)="backToProgression()"
          aria-label="Torna alla pagina di progressione della registrazione"
        ></button>
      </div>
    </div>
  }
</div>
